'use client'

import { useRef, useEffect, useState } from 'react'
import { motion, useMotionValue, useTransform } from 'framer-motion'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import {
  Body,
  ButtonLG,
  Card,
  Container,
  Reveal,
  Section,
  SectionHeader,
  TerminalBox,
} from 'design-studio'
import { NavigationData } from '@/components/data-links'
import { StepTitle } from '@/components/ui/step-badge'
import { SL_STEP_CARD_SURFACE } from '@/components/theme'

const MotionCard = motion.create(Card)

const stepCardProps = {
  surface: 'glass' as const,
  glassVariant: 'neutral' as const,
  radius: 'xl' as const,
  padding: 'lg' as const,
  className: SL_STEP_CARD_SURFACE,
}

const AGENT_PROMPT_COPY =
  'use SpecLogician to discover the states and transitions in this service'

export function GettingStartedSteps() {
  const gridRef = useRef<HTMLDivElement>(null)
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    queueMicrotask(() => setMounted(true))
  }, [])
  const isDark = mounted && resolvedTheme === 'dark'

  const scrollYProgress = useMotionValue(0)

  useEffect(() => {
    const el = gridRef.current
    if (!el) return

    const update = () => {
      const top = el.getBoundingClientRect().top
      const vh = window.innerHeight
      const progress =
        1 - Math.min(1, Math.max(0, (top - vh * 0.5) / (vh * 0.5)))
      scrollYProgress.set(progress)
    }

    window.addEventListener('scroll', update, { passive: true })
    update()
    return () => window.removeEventListener('scroll', update)
  }, [scrollYProgress])

  const card2Y = useTransform(scrollYProgress, [0, 1], ['33%', '0%'])
  const card3Y = useTransform(scrollYProgress, [0, 1], ['66%', '0%'])

  const card2Opacity = useTransform(scrollYProgress, [0, 1], [0.65, 1])
  const card3Opacity = useTransform(scrollYProgress, [0, 1], [0.35, 1])

  const BORDER_BRIGHT = 'rgba(96, 165, 250, 0.85)'
  const BORDER_MID = 'rgba(96, 165, 250, 0.425)'
  const BORDER_FAINT = 'rgba(96, 165, 250, 0.255)'
  const BORDER_DIM = 'rgba(30, 58, 138, 0.25)'

  const card1Border = useTransform(
    scrollYProgress,
    [0, 0.14, 0.3],
    [BORDER_BRIGHT, BORDER_BRIGHT, BORDER_FAINT],
  )
  const card2Border = useTransform(
    scrollYProgress,
    [0.08, 0.24, 0.36, 0.5],
    [BORDER_DIM, BORDER_BRIGHT, BORDER_BRIGHT, BORDER_FAINT],
  )
  const card3Border = useTransform(
    scrollYProgress,
    [0.32, 0.52, 0.72, 1],
    [BORDER_DIM, BORDER_BRIGHT, BORDER_BRIGHT, BORDER_BRIGHT],
  )

  return (
    <Section
      id="getting-started"
      className="relative scroll-mt-19 bg-zinc-200 backdrop-blur-md dark:bg-slate-800/10"
    >
      <Container className="relative z-10 max-w-7xl py-16 sm:pt-24 sm:pb-32">
        <Reveal className="relative mx-auto mb-6">
          <SectionHeader
            pretitle="Start now"
            pretitleColor="text-teal-500"
            title="How to Get Started"
            description="Go from sign-up to discovering the behavioral model of your software in four steps."
          />
        </Reveal>

        <motion.div
          ref={gridRef}
          className="relative z-20 grid grid-cols-1 gap-3 lg:grid-cols-3"
        >
          <MotionCard
            {...stepCardProps}
            style={isDark ? { borderColor: card1Border } : undefined}
          >
            <p className="text-slate-700 dark:text-slate-300 dark:text-shadow-sm">
              <StepTitle number={1} title="Sign up and get an API key" />
              <br />
              Your first step is to obtain an{' '}
              <a
                href="https://universe.imandra.ai/#pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline underline-offset-2 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Imandra Universe API key
              </a>
              , which gives you access to SpecLogician and other tools in the
              Imandra Universe ecosystem.
            </p>
            <ButtonLG
              href={NavigationData.signup.href}
              variant="none"
              ariaLabel={NavigationData.signup['aria-label']}
              classNames="lg:ml-auto bg-teal-700 font-bold whitespace-nowrap text-white hover:bg-teal-600 max-w-max"
            >
              Sign up
            </ButtonLG>
          </MotionCard>

          <motion.div
            style={{ y: card2Y, opacity: card2Opacity }}
            className="h-full"
          >
            <MotionCard
              {...stepCardProps}
              style={isDark ? { borderColor: card2Border } : undefined}
            >
              <p className="text-slate-700 dark:text-slate-300 dark:text-shadow-sm">
                <StepTitle number={2} title="Install locally" />
              </p>
              <div className="space-y-2">
                <TerminalBox
                  className="text-xs leading-snug"
                  copyContent="curl -fsSL https://speclogician.dev/speclogician/install.sh | sh"
                >
                  <span className="my-1 block">
                    <span className="text-teal-600 dark:text-teal-400">
                      curl
                    </span>
                    <span className="text-orange-600 dark:text-orange-400">
                      {' '}
                      -fsSL{' '}
                    </span>
                    <span className="text-blue-600 dark:text-blue-400">
                      speclogician.dev/speclogician/install.sh
                    </span>
                    <span className="text-slate-400 dark:text-slate-500">
                      {' '}
                      |{' '}
                    </span>
                    <span className="text-teal-600 dark:text-teal-400">sh</span>
                  </span>
                </TerminalBox>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Includes Python 3.13
                </p>
              </div>
              <Body size="sm" tone="muted" className="dark:text-shadow-sm">
                Install SpecLogician locally — the script sets up uv and Python
                automatically so your workspace can build models, discover
                artifacts, and run analysis.
              </Body>
            </MotionCard>
          </motion.div>

          <motion.div
            style={{ y: card3Y, opacity: card3Opacity }}
            className="h-full"
          >
            <MotionCard
              {...stepCardProps}
              style={isDark ? { borderColor: card3Border } : undefined}
            >
              <p className="text-slate-700 dark:text-slate-300 dark:text-shadow-sm">
                <StepTitle number={3} title="Install the extension" />
              </p>
              <TerminalBox
                className="w-full"
                copyButton={false}
                copyContent="Install the SpecLogician extension in VS Code, Cursor, or Antigravity"
              >
                <span className="my-1 block">
                  <span className="text-slate-500 dark:text-slate-400">
                    Install the{' '}
                  </span>
                  <span className="text-blue-600 dark:text-blue-400">
                    SpecLogician
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {' '}
                    extension in{' '}
                  </span>
                  <span className="text-teal-600 dark:text-teal-400">
                    VS Code / Cursor / Antigravity
                  </span>
                </span>
              </TerminalBox>
              <Body size="sm" tone="muted" className="dark:text-shadow-sm">
                The extension works with your local installation to visualize
                states, transitions, graphs, scenarios, and related artifacts
                directly in your editor.
              </Body>
            </MotionCard>
          </motion.div>
        </motion.div>

        <Reveal className="mx-auto mt-12 max-w-6xl">
          <div className="grid items-center gap-6 lg:grid-cols-2 lg:gap-8">
            <div>
              <StepTitle number={4} title="Ask your AI agent" />
              <Body size="sm" tone="muted" className="dark:text-shadow-sm">
                With SpecLogician installed, point your coding agent at the
                service you care about. It can discover the model, inspect
                scenarios, and reason about behavior instead of guessing from
                local code alone.
              </Body>
            </div>
            <TerminalBox className="w-full" copyContent={AGENT_PROMPT_COPY}>
              <span className="my-1 block text-sm leading-relaxed">
                <span className="text-slate-400 dark:text-slate-500">
                  Claude / GPT / Gemini / Cursor:{' '}
                </span>
                <span className="text-teal-600 dark:text-teal-400">
                  use SpecLogician
                </span>{' '}
                to discover the{' '}
                <span className="text-blue-600 dark:text-blue-400">
                  states and transitions
                </span>{' '}
                in this service
              </span>
            </TerminalBox>
          </div>
        </Reveal>

        <Reveal className="mx-auto mt-12 max-w-6xl text-base text-slate-600 dark:text-slate-300">
          Read the full{' '}
          <Link
            href="/docs/getting-started/"
            className="text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
          >
            getting started guide here
          </Link>
          .
        </Reveal>
      </Container>
    </Section>
  )
}
